import { AxiosError } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  Message,
  Segment,
} from 'semantic-ui/semantic-ui-imports';
import { useAuth } from '../../contexts/auth';
import axios, { refreshCsrfToken } from '../../services/request';
import './index.scss';

export interface LoginProps {
  onLoggedIn: () => void;
  onNavigateAway?: () => void;
}

function Login({ onLoggedIn, onNavigateAway }: LoginProps) {
  // translation
  const { t } = useTranslation();
  const { setToken } = useAuth();
  const navigate = useNavigate();

  // state handling
  const [password, setPassword] = React.useState<string | undefined>();
  const [email, setEmail] = React.useState<string | undefined>();
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [isInvalid, setInvalid] = React.useState<boolean>(false);
  const [isNotVerified, setIsNotVerified] = React.useState<boolean>(false);

  // validation of email and password
  const onSubmit = async () => {
    setLoading(true);
    setIsNotVerified(false);
    setInvalid(false);

    const params = {
      email,
      password,
    };

    await refreshCsrfToken();

    try {
      const { status, data } = await axios.post(`/rest-auth/login/`, params);
      if (status === 200) {
        // TODO: save token in context and redirect to homepage
        setToken(data.key);

        // Refresh CSRF token
        await refreshCsrfToken();

        onLoggedIn();
      } else {
        setInvalid(true);
        // TODO: show error message in dom
      }
    } catch (error) {
      const err: AxiosError = error as AxiosError;
      const data = err?.response?.data;
      if (
        data?.non_field_errors &&
        data?.non_field_errors[0] === 'E-mail is not verified.'
      ) {
        setIsNotVerified(true);
      } else {
        setInvalid(true);
      }
    }
    setLoading(false);
  };

  return (
    <div className="login">
      <Form className="login__form" onSubmit={onSubmit}>
        <Segment>
          <Form.Input
            name="email"
            label={t('loginPage.email')}
            fluid
            icon="user"
            iconPosition="left"
            placeholder={t('loginPage.emailPlaceholder')}
            onChange={(e: any, { value }: any) => setEmail(value)}
          />
          <Form.Input
            name="password"
            label={t('loginPage.passwordPlaceholder')}
            fluid
            icon="lock"
            iconPosition="left"
            placeholder={t('loginPage.passwordPlaceholder')}
            type="password"
            onChange={(e: any, { value }: any) => setPassword(value)}
          />
          {isInvalid && (
            <Message negative>
              <p>{t('loginPage.errorText')}</p>
            </Message>
          )}
          {isNotVerified && (
            <Message negative>
              <p>{t('loginPage.notVerified')}</p>
            </Message>
          )}
          <Button
            className="login__button"
            type="submit"
            color="teal"
            fluid
            size="large"
            disabled={isLoading}
            loading={isLoading}
          >
            {t('loginPage.buttonText')}
          </Button>
        </Segment>
      </Form>
      <Message>
        <p>
          {t('loginPage.signUpQuestion')}?{' '}
          <a
            onClick={() => {
              if (onNavigateAway) {
                onNavigateAway();
              }
              navigate('/register');
            }}
          >
            {t('loginPage.signUpLink')}
          </a>
        </p>
      </Message>
      <Message>
        <p>
          {t('loginPage.resetPasswordQuestion')}?{' '}
          <a
            onClick={() => {
              if (onNavigateAway) {
                onNavigateAway();
              }
              navigate('/resetpassword');
            }}
          >
            {t('loginPage.resetPasswordLink')}
          </a>
        </p>
      </Message>
    </div>
  );
}

export default Login;
